import { useEffect, useState } from 'react';
import { Box, IconButton, styled, Typography } from '@mui/material';
import { IconApproved, IconDuplicate } from 'assets/img';
import { CopyFieldProps } from './copy-field.types';

const COPIED_RESET_MS = 2000;

export const CopyField = ({
  label,
  value,
  copyLabel,
  copiedLabel,
  'data-testid': dataTestId,
}: CopyFieldProps) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_RESET_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copied]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
    });
  };

  return (
    <Wrapper data-testid={dataTestId}>
      <Typography variant="subtitle2" color="secondary">
        {label}
      </Typography>
      <ValueBox>
        <ValueText variant="monospace">{value}</ValueText>
        <CopyButton
          type="button"
          onClick={handleCopy}
          aria-label={copied ? copiedLabel : copyLabel}
          size="small"
        >
          {copied ? <IconApproved /> : <IconDuplicate />}
        </CopyButton>
      </ValueBox>
    </Wrapper>
  );
};

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

const ValueBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(2, 4),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.lwBlack[10]}`,
  backgroundColor: theme.palette.lwBlack[6],
}));

const ValueText = styled(Typography)({
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

const CopyButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1),
  '& svg path[fill]': {
    fill: theme.palette.lwBlack[40],
  },
  '& svg path[stroke]': {
    stroke: theme.palette.lwBlack[40],
  },
  '&:hover': {
    backgroundColor: theme.palette.lwBlack[10],
    '& svg path[fill]': {
      fill: theme.palette.lwBlack[60],
    },
    '& svg path[stroke]': {
      stroke: theme.palette.lwBlack[60],
    },
  },
}));
